// Keep Shopify's original account sheet; prepare it only on account intent.
class SkullDeferredAccount extends HTMLElement {
  #events;
  #ready;
  #timer;
  #rejectReady;
  #opening = false;

  connectedCallback() {
    this.#events?.abort();
    this.#events = new AbortController();
    const options = { signal: this.#events.signal };
    const prepare = () => { this.#prepare().catch(() => {}); };
    this.addEventListener('pointerenter', prepare, options);
    this.addEventListener('pointerdown', prepare, options);
    this.addEventListener('focusin', prepare, options);
    this.addEventListener('click', this.#open, options);
  }

  disconnectedCallback() {
    this.#events?.abort();
    clearTimeout(this.#timer);
    this.#rejectReady?.(new Error('Account detached'));
    this.#rejectReady = null;
    this.#ready = null;
    this.#opening = false;
  }

  #prepare() {
    if (this.#ready) return this.#ready;
    const template = this.querySelector('template[data-account-template]');
    if (template) {
      const fragment = template.content.cloneNode(true);
      fragment.querySelector('shopify-account').setAttribute('data-preparing', '');
      template.replaceWith(fragment);
    }
    const promise = new Promise((resolve, reject) => {
      this.#rejectReady = reject;
      const deadline = performance.now() + 8000;
      const poll = () => {
        const account = this.querySelector('shopify-account');
        // signed-out-avatar is a documented Shopify CSS part.
        const button = account?.shadowRoot?.querySelector('button[part~="signed-out-avatar"]');
        if (button) {
          this.#rejectReady = null;
          resolve(button);
        } else if (!this.isConnected || performance.now() >= deadline) {
          this.#rejectReady = null;
          reject(new Error('Account unavailable'));
        } else {
          this.#timer = setTimeout(poll, 40);
        }
      };
      poll();
    }).catch(error => { if (this.#ready === promise) this.#ready = null; throw error; });
    this.#ready = promise;
    return promise;
  }

  #open = async event => {
    const link = event.target.closest?.('[data-account-fallback]');
    if (!link || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (this.#opening) return;
    this.#opening = true;
    const lifecycle = this.#events;
    link.setAttribute('aria-busy', 'true');
    try {
      const button = await this.#prepare();
      if (this.isConnected && !lifecycle.signal.aborted) {
        this.querySelector('shopify-account')?.removeAttribute('data-preparing');
        link.remove();
        button.focus({ preventScroll: true });
        button.click();
      }
    } catch (_) {
      // Native account navigation remains available if the enhanced sheet fails.
      if (this.isConnected && !lifecycle.signal.aborted) window.location.assign(link.href);
    } finally {
      if (this.#events === lifecycle) this.#opening = false;
      link.removeAttribute('aria-busy');
    }
  };
}
if (!customElements.get('skull-deferred-account')) customElements.define('skull-deferred-account', SkullDeferredAccount);
